import styled from "@emotion/styled/macro";
import {
  Typography,
  tokens,
  Select,
  Button,
} from "@sunrun/experience-ui-components";
import { StatusChip } from "../atoms/StatusChip";
import { Card, CardContent, CardHeader, CardSectionDivider } from "./Card";
import { INTERVAL_DATA_DECLINE_REASONS } from "fixtures/IntervalDataDeclineReasons";

type IntervalDataCardProps = {
  intervalDataDeclineReason: string;
  setIntervalDataDeclineReason: (reason: string) => void;
  completed: boolean;
  isUpdatingIntervalData: boolean;
  handleIntervalDataUpdate: () => void;
  intervalDataUsed: boolean;
};

const IntervalDataCard = ({
  intervalDataDeclineReason,
  setIntervalDataDeclineReason,
  completed,
  isUpdatingIntervalData,
  handleIntervalDataUpdate,
  intervalDataUsed,
}: IntervalDataCardProps) => (
  <Card>
    <CardHeader>
      <Typography size={tokens.FONT_SIZE_3} color={tokens.BRAND_HEROBLUE}>
        Interval Data Decline Reason
      </Typography>

      <StatusChip
        status={completed ? "completed" : "readyToComplete"}
        isMobile={false}
      />
    </CardHeader>
    <CardSectionDivider />
    <CardContent>
      {intervalDataUsed ? (
        <Typography
          color={tokens.TINTS_OFF_WHITE_20}
          style={{ marginBottom: "16px" }}
        >
          Interval data required by the CPUC has been collected via the Get
          Utility Bill Online feature.
        </Typography>
      ) : (
        <>
          <Typography
            color={tokens.TINTS_OFF_WHITE_20}
            style={{ marginBottom: "16px" }}
          >
            This information is required by the CPUC when hourly interval data
            has not been collected via the Get Utility Bill Online feature.
          </Typography>

          <SelectContainer>
            <Typography
              color={tokens.TINTS_OFF_WHITE_20}
              style={{ marginBottom: "8px" }}
            >
              Interval data decline reason
            </Typography>
            <StyledSelect
              value={completed ? "" : intervalDataDeclineReason || ""}
              placeholder={
                completed ? intervalDataDeclineReason : "Select a reason"
              }
              options={[
                ...INTERVAL_DATA_DECLINE_REASONS.map((reason) => ({
                  value: reason,
                  label: reason,
                })),
              ]}
              onChange={({ target }) =>
                setIntervalDataDeclineReason(target.value)
              }
              disabled={isUpdatingIntervalData || completed}
              data-testid="interval-data-decline-reason-select"
            />
          </SelectContainer>
          <ButtonContainer>
            <StyledButton
              onClick={handleIntervalDataUpdate}
              disabled={
                isUpdatingIntervalData ||
                !intervalDataDeclineReason ||
                completed
              }
              data-testid="interval-data-decline-save"
            >
              {isUpdatingIntervalData ? "Saving..." : "Save"}
            </StyledButton>
          </ButtonContainer>
        </>
      )}
    </CardContent>
  </Card>
);

const SelectContainer = styled.div`
  margin-bottom: 24px;
`;

const StyledSelect = styled(Select)`
  width: 100%;
`;

const StyledButton = styled(Button)`
  padding: 12px 24px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

export { IntervalDataCard };
